import {
  ACTIVE_TRADING_ADDRESS_KEY,
  ROOT_TOKEN_LAST_REQUESTED_KEY,
  ROOT_TOKEN_SESSION_KEY,
  SUB_ACCOUNT_SESSION_KEY,
  TERMS_ACCEPTED_KEY,
  VAULT_CONTRACT_ADDRESS_KEY,
} from "@constants";
import { indexDB } from "@utils/indexDB";

import type { SubAccount } from "../account/types";

export class StorageService {
  getRootToken = async (): Promise<string | null> => {
    return indexDB.get<string>(ROOT_TOKEN_SESSION_KEY);
  };

  setRootToken = async (token: string): Promise<void> => {
    await indexDB.set(ROOT_TOKEN_SESSION_KEY, token);
  };

  getRootTokenLastRequested = async (): Promise<number | null> => {
    return indexDB.get<number>(ROOT_TOKEN_LAST_REQUESTED_KEY);
  };

  setRootTokenLastRequested = async (timestamp: number): Promise<void> => {
    await indexDB.set(ROOT_TOKEN_LAST_REQUESTED_KEY, timestamp);
  };

  getSubAccounts = async (): Promise<Map<string, SubAccount[]>> => {
    const stored = await indexDB.get<[string, SubAccount[]][] | string>(SUB_ACCOUNT_SESSION_KEY);

    if (!stored) {
      return new Map();
    }

    const parsed = typeof stored === "string" ? (JSON.parse(stored) as [string, SubAccount[]][]) : stored;

    if (Array.isArray(parsed)) {
      return new Map(parsed);
    }

    return new Map();
  };

  setSubAccounts = async (subAccounts: Map<string, SubAccount[]>): Promise<void> => {
    await indexDB.set(SUB_ACCOUNT_SESSION_KEY, Array.from(subAccounts.entries()));
  };

  getActiveTradingAddress = async (): Promise<string | null> => {
    return indexDB.get<string>(ACTIVE_TRADING_ADDRESS_KEY);
  };

  setActiveTradingAddress = async (address: string): Promise<void> => {
    await indexDB.set(ACTIVE_TRADING_ADDRESS_KEY, address);
  };

  clear = async (preserveData?: { vaultContractKey?: string }): Promise<void> => {
    try {
      await indexDB.clear();
    } catch {
      // Failed to clear IndexDB
    }

    try {
      const termsAccepted = localStorage.getItem(TERMS_ACCEPTED_KEY);
      localStorage.clear();
      if (termsAccepted) {
        localStorage.setItem(TERMS_ACCEPTED_KEY, termsAccepted);
      }
      if (preserveData?.vaultContractKey) {
        localStorage.setItem(VAULT_CONTRACT_ADDRESS_KEY, preserveData.vaultContractKey);
      }
    } catch {
      // Failed to clear localStorage
    }
  };
}

export const storageService = new StorageService();
